import React, { useState } from 'react';
import { Row, Col, Form, Input, Label } from 'reactstrap';
import { AccountFormData } from '../../types/types';
import FormInput from '../common/FormInput';
import SelectInput from '../common/SelectInput';
import AccountOwnerModal, { AccountOwnerData } from '../modals/AccountOwnerModal';
import ContactModal, { ContactData } from '../modals/ContactModal';
import AssociateOwnerModal, { AssociateOwnerData } from '../modals/AssociateOwnerModal';

interface ContactInformationProps {
  formData: AccountFormData;
  setFormData: (data: AccountFormData) => void;
}

const ContactInformation: React.FC<ContactInformationProps> = ({ formData, setFormData }) => {
  const [isAccountOwnerModalOpen, setIsAccountOwnerModalOpen] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isAssociateOwnerModalOpen, setIsAssociateOwnerModalOpen] = useState(false);

  const handleInputChange = (field: keyof AccountFormData) => (value: string) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleCheckboxChange = (field: keyof AccountFormData) => (checked: boolean) => {
    setFormData({ ...formData, [field]: checked });
  };

  const handleSearchClick = (field: string) => {
    if (field === 'parentAccount') {
      setIsAccountOwnerModalOpen(true);
    } else if (field === 'primaryContact') {
      setIsContactModalOpen(true);
    } else if (field === 'accountOwner') {
      setIsAssociateOwnerModalOpen(true);
    } else {
      console.log(`Search clicked for ${field}`);
      // TODO: Implement other search modals
    }
  };

  const handleAccountOwnerSelect = (accountOwner: AccountOwnerData) => {
    setFormData({ 
      ...formData, 
      parentAccount: accountOwner.name 
    });
    setIsAccountOwnerModalOpen(false);
  };

  const handleContactSelect = (contact: ContactData) => {
    setFormData({ 
      ...formData, 
      primaryContact: `${contact.firstName} ${contact.lastName}` 
    });
    setIsContactModalOpen(false);
  };

  const handleAssociateOwnerSelect = (associate: AssociateOwnerData) => {
    setFormData({ 
      ...formData, 
      accountOwner: `${associate.firstName} ${associate.lastName}` 
    });
    setIsAssociateOwnerModalOpen(false);
  };

  const handleAccountOwnerModalClose = () => {
    setIsAccountOwnerModalOpen(false);
  };

  const handleContactModalClose = () => {
    setIsContactModalOpen(false);
  };

  const handleAssociateOwnerModalClose = () => {
    setIsAssociateOwnerModalOpen(false);
  };

  return (
    <>
      <fieldset className="border p-2">
        <legend className="float-none w-auto px-2 mb-0 text-body" style={{ fontSize: '0.9rem' }}>Contact Information</legend>
        <Form>
          <Row>
            <Col md={6}>
              <FormInput
                id="mainPhoneNumber"
                label="Main Phone Number"
                value={formData.mainPhoneNumber}
                onChange={handleInputChange('mainPhoneNumber')}
                type="tel"
              />
            </Col>
            <Col md={6}>
              <FormInput
                id="mainFaxNumber"
                label="Main Fax Number"
                value={formData.mainFaxNumber}
                onChange={handleInputChange('mainFaxNumber')}
                type="tel"
              />
            </Col>
            <Col md={6}>
              <FormInput
                id="mainEmailAddress"
                label="Main Email Address"
                value={formData.mainEmailAddress}
                onChange={handleInputChange('mainEmailAddress')}
                type="email"
              />
            </Col>
            <Col md={6}>
              <FormInput
                id="webAddress"
                label="Web Address"
                value={formData.webAddress}
                onChange={handleInputChange('webAddress')}
              />
            </Col>
            <Col md={6}>
              <FormInput
                id="accountOwner"
                label="Account Owner"
                value={formData.accountOwner}
                onChange={handleInputChange('accountOwner')}
                hasSearchButton
                onSearchClick={() => handleSearchClick('accountOwner')}
              />
            </Col>
            <Col md={6}>
              <FormInput
                id="primaryContact"
                label="Primary Contact"
                value={formData.primaryContact}
                onChange={handleInputChange('primaryContact')}
                hasSearchButton
                onSearchClick={() => handleSearchClick('primaryContact')}
              />
            </Col>
            <Col md={6}>
              <FormInput
                id="parentAccount"
                label="Parent Account"
                value={formData.parentAccount}
                onChange={handleInputChange('parentAccount')}
                hasSearchButton
                onSearchClick={() => handleSearchClick('parentAccount')}
              />
            </Col>
            <Col md={6}>
              <SelectInput
                formData={formData}
                setFormData={setFormData}
                label="Payment Terms"
                id="paymentTerms"
                options={['COD', 'Net 7', 'Net 15', 'Net 30', 'Net 60', 'Net 90', 'Prepaid']}
              />
            </Col>
            <Col md={6}>
              <div className="form-check mt-4">
                <Input
                  type="checkbox"
                  className="form-check-input"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={(e) => handleCheckboxChange('isActive')(e.target.checked)}
                />
                <Label className="form-check-label" htmlFor="isActive">
                  Active
                </Label>
              </div>
            </Col>
            <Col md={6}>
              <div className="form-check mt-4">
                <Input
                  type="checkbox"
                  className="form-check-input"
                  id="creditStatus"
                  checked={formData.creditStatus}
                  onChange={(e) => handleCheckboxChange('creditStatus')(e.target.checked)}
                />
                <Label className="form-check-label" htmlFor="creditStatus">
                  Credit Status
                </Label>
              </div>
            </Col>
            <Col md={6}>
              <div className="form-check mt-4">
                <Input
                  type="checkbox"
                  className="form-check-input"
                  id="isSupplier"
                  checked={formData.isSupplier}
                  onChange={(e) => handleCheckboxChange('isSupplier')(e.target.checked)}
                />
                <Label className="form-check-label" htmlFor="isSupplier">
                  Supplier
                </Label>
              </div>
            </Col>
          </Row>
        </Form>
      </fieldset>

      {/* Account Owner Modal for Parent Account */}
      <AccountOwnerModal
        isOpen={isAccountOwnerModalOpen}
        onClose={handleAccountOwnerModalClose}
        onSelect={handleAccountOwnerSelect}
      />

      {/* Contact Modal for Primary Contact */}
      <ContactModal
        isOpen={isContactModalOpen}
        onClose={handleContactModalClose}
        onSelect={handleContactSelect}
      />

      {/* Associate Owner Modal for Account Owner */}
      <AssociateOwnerModal
        isOpen={isAssociateOwnerModalOpen}
        onClose={handleAssociateOwnerModalClose}
        onSelect={handleAssociateOwnerSelect}
      />
    </>
  );
};

export default ContactInformation; 